import { Injectable } from '@angular/core';
import { HttpClient, HttpHandler, HttpParams, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { AuthenticationService } from '../../../authentication/authentication.service';
import { environment } from '../../../../environments/environment';
import { Router } from '@angular/router';
import { TranslationService } from '../../../common/services/languages/translation.service';
import { NgRedux } from '@angular-redux/store';
import { AppState } from '../../../store/state.model';
import { AuthDispatcher } from '../../../store/auth/auth-dispatcher';

/** Creates custom http calls get or post */
export class CustomHttpParams extends HttpParams {
  constructor(public spec: string) {
    super();
  }
}
/** Handles the Api calls with the body or header deired
 * allow the client perform custom logic on the error/succes payloads
 */
@Injectable()
export class VcmApiHandlerService extends HttpClient {
  /**
  * @var This property will determine the type of the api call
  **/
  private requestType: string;
  constructor(
    private httpHandler: HttpHandler,
    private authService: AuthenticationService,
    private authDispatcher: AuthDispatcher,
    public reduxState: NgRedux<AppState>,
    public router: Router,
    public translate: TranslationService,
    public ngRedux: NgRedux<AppState>
  ) {
    super(httpHandler);
  }

  /**
  * @event intercept Intercept any request that is triggered by APIHandler and sets the common header of the ORCH layer
  * @returns The response that is being intercepted so it can continue to the Network call
  **/
  getTypeParams() {
    return (!!this.requestType) ?
      new CustomHttpParams(this.requestType) : new CustomHttpParams('default');
  }
  /** This method gets a request to the passed url and options */
  public getRequest(url: string, options?: any): Observable<any> {
    const headers: HttpHeaders = this.getHeaders(options);
    return super.get(`${environment.adminportal_GW}${url}`, {
      headers: headers,
      params: this.getTypeParams()
    }).pipe(
      map((res: any) => {
        return (res);
      }),
      catchError(this.handleError.bind(this))
    );
  }
  /** This method posts a request to the passed url, body and options */
  public postRequest(url: string, body: any | null, options?: any): Observable<any> {
    const headers: HttpHeaders = this.getHeaders(options);
    return super.post(`${environment.adminportal_GW}${url}`, Object.assign(body), {
      headers: headers,
      params: this.getTypeParams()
    }).pipe(
      map((res: any) => {
        return (res);
      }),
      catchError(this.handleError.bind(this))
    );
  }
  /** This method posts a request to the passed url, body and options */
  public putRequest(url: string, body: any | null, options?: any): Observable<any> {
    const headers: HttpHeaders = this.getHeaders(options);
    return super.put(`${environment.adminportal_GW}${url}`, Object.assign(body), {
      headers: headers,
      params: this.getTypeParams()
    }).pipe(
      map((res: any) => {
        return (res);
      }),
      catchError(this.handleError.bind(this))
    );
  }
  /** This method deletes a request to the passed url and options */
  public deleteRequest(url: string, options?: any): Observable<any> {
    const headers: HttpHeaders = this.getHeaders(options);
    return super.delete(`${environment.adminportal_GW}${url}`, {
      headers: headers,
      params: this.getTypeParams()
    }).pipe(
      map((res: any) => {
        return (res);
      }),
      catchError(this.handleError.bind(this))
    );
  }

  /** This method adds the custom headers taken from the user input to the http request */
  private getHeaders(data: { type: string, params: any }): HttpHeaders {
    this.requestType = (!!data && !!data.type) ? data.type : 'default';
    let headers = new HttpHeaders();
    if (!!data && !!data.params) {
      const params = data.params;
      switch (data.type) {
        case 'get_vcm_history':
          headers = !!params.vin ? headers.set('vin', params.vin) : headers;
          headers = !!params.chipId ? headers.set('chip-id', params.chipId) : headers;
          break;
        case 'clear_vin':
          headers = !!params.vin ? headers.set('vin', params.vin) : headers;
          break;
        case 'clear_chip_id':
          headers = !!params.vin ? headers.set('vin', params.vin) : headers;
          break;
        case 'blacklist_vin':
          const authDetails = this.ngRedux.getState().auth;
          const userName = this.ngRedux.getState().auth.name.split('(')[0].trim();
          headers = !!params.vin ? headers.set('vin', params.vin) : headers;
          headers = !!params.vin ? headers.set('x-ctp-hucom-user', !!userName ? userName : '') : headers;
          headers = !!params.vin ? headers.set('x-ctp-hucom-user-email', !!authDetails.email ? authDetails.email : '') : headers;
          headers = !!params.vin ? headers.set('x-ctp-hucom-role', 'VCM_ADMIN') : headers;
          break;
        case 'cvs_details':
          headers = !!params.vin ? headers.set('vin', params.vin) : headers;
          headers = !!params.chipId ? headers.set('chip-id', params.chipId) : headers;
          headers = headers.set('language', this.getLanguage());
          break;
      }
    }
    return headers;
  }
  /** This method returns the language header for the  preview api*/
  private getLanguage(): string {
    const lang = this.translate.getCurrentLanguage();
    return lang.includes('en') ? 'en-US' : lang.includes('fr') ? 'fr-CA' : 'es-PR';
  }
  /** This method handles the errors of a http request*/
  private handleError(err: HttpErrorResponse): Observable<ArrayBuffer> {
    if (this.isAuthenticationError(err)) {
      return throwError(() => ({ errorCode: 'UNAUTHORIZED' }));
    }
    let errorResponse = {};
    if (this.checkErrorContent(err)) {
      const errorCode = err.error.status.messages.map(error => error.responseCode);
      const throwObj = (!!errorCode && errorCode.length) ? errorCode : [err.status];
      const errorMessage = err.error.status.messages.map(error => error.detailedDescription);
      const url = err.url;
      // Extract API path by removing the gateway domain prefix
      const apiPath = url ? url.substring(url.indexOf('/v')) : '';
      errorResponse = { 'success': false, errorCode: throwObj[0], errorMessage: errorMessage[0], api: apiPath };
    }
    return throwError(() => errorResponse);
  }
  /** This method determines if it is an authentication error from the api gateway*/
  private isAuthenticationError(err: HttpErrorResponse): boolean {
    if ((this.checkErrorContent(err) && err.error.status.messages[0].detailedDescription.indexOf('expired') > 0) ||
      (err.status === 0 && err.ok === false && err.statusText === 'Unknown Error') ||
      (err.status === 403 && this.checkErrorContent(err) && err.error.status.messages[0].responseCode === 'APIGW-403')) {
      this.routeError();
      this.authDispatcher.logout();
    }
    return (err.status === 0 && err.ok === false && err.statusText === 'Unknown Error') ||
      (err.status === 403 && this.checkErrorContent(err) && err.error.status.messages[0].responseCode === 'APIGW-403');
  }
  /** This checks the structure of the error if there is a status object*/
  private checkErrorContent(err: HttpErrorResponse): boolean {
    return err.error && err.error.status && err.error.status.messages && err.error.status.messages.length;
  }
  /*
  * @function routeError routes to error page
  * @returns void
  **/
  private routeError(): void {
    this.router.navigate(['dashboard/error'], { queryParams: {}, skipLocationChange: true });
  }
}
